import { useState, useRef, useEffect } from "react";
import { X, Check, ChevronDown } from "lucide-react";

interface Categoria {
  id: string;
  nome: string;
}

interface DropdownCategoriaPesquisavelProps {
  categorias: Categoria[];
  valorSelecionado: string;
  onSelecionar: (id: string) => void;
}

/**
 * Dropdown de categoria com campo de pesquisa interno. Usado tanto no
 * catálogo do usuário comum quanto na tela de Gerenciar Livros do admin
 * (substituindo os chips sempre visíveis, que ocupavam várias linhas com
 * muitas categorias cadastradas).
 */
export function DropdownCategoriaPesquisavel({
  categorias,
  valorSelecionado,
  onSelecionar,
}: DropdownCategoriaPesquisavelProps) {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const nomeSelecionado =
    valorSelecionado === "todas"
      ? "Todas as categorias"
      : categorias.find((c) => c.id === valorSelecionado)?.nome ?? "Todas as categorias";

  const categoriasFiltradas = categorias.filter((c) =>
    c.nome.toLowerCase().includes(termo.toLowerCase()),
  );

  useEffect(() => {
    function handleClickFora(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
        setTermo("");
      }
    }
    document.addEventListener("mousedown", handleClickFora);
    return () => document.removeEventListener("mousedown", handleClickFora);
  }, []);

  function selecionar(id: string) {
    onSelecionar(id);
    setAberto(false);
    setTermo("");
  }

  return (
    <div ref={containerRef} className="relative w-full sm:w-64">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full items-center justify-between rounded-full bg-brand-verde px-4 py-1.5 text-left text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-brand-verde/40"
      >
        <span className="truncate">{nomeSelecionado}</span>
        <ChevronDown size={14} className={`ml-1 shrink-0 transition-transform ${aberto ? "rotate-180" : ""}`} />
      </button>

      {aberto && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-border bg-surface p-2 shadow-lg">
          <input
            type="text"
            autoFocus
            placeholder="Pesquisar categoria..."
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 text-xs text-text placeholder:text-muted focus:border-brand-verde focus:outline-none"
          />

          {/* Só aparece quando há uma categoria específica selecionada —
              evita repetir "Todas as categorias" como se fosse mais um
              item da lista (era confuso: aparecia duas vezes seguidas). */}
          {valorSelecionado !== "todas" && (
            <button
              type="button"
              onClick={() => selecionar("todas")}
              className="mt-2 flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-left text-xs font-bold text-brand-verde hover:bg-surface-2"
            >
              <X size={12} />
              Limpar filtro (ver todas)
            </button>
          )}

          <ul className="mt-2 max-h-48 overflow-y-auto">
            {categoriasFiltradas.map((cat) => {
              const selecionada = cat.id === valorSelecionado;
              return (
                <li key={cat.id}>
                  <button
                    type="button"
                    onClick={() => selecionar(cat.id)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs ${
                      selecionada
                        ? "bg-surface-2 font-bold text-brand-verde"
                        : "font-medium text-text hover:bg-surface-2"
                    }`}
                  >
                    {cat.nome}
                    {selecionada && <Check size={13} />}
                  </button>
                </li>
              );
            })}
            {!categoriasFiltradas.length && (
              <li className="px-3 py-2 text-xs text-muted">Nenhuma categoria encontrada.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
